import { useState } from 'react';
import { cn } from '../utils/cn';
import {
  Play,
  Pause,
  RotateCcw,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  BarChart3,
  Settings,
  Download,
  ChevronDown,
  Clock,
  Percent,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Layers,
  Zap,
} from 'lucide-react';

interface BacktestResult {
  totalReturn: number;
  winRate: number;
  maxDrawdown: number;
  sharpeRatio: number;
  totalTrades: number;
  profitableTrades: number;
  avgWin: number;
  avgLoss: number;
  largestWin: number;
  largestLoss: number;
  profitFactor: number;
}

interface Trade {
  id: string;
  timestamp: string;
  type: 'buy' | 'sell';
  price: number;
  amount: number;
  pnl: number;
  cumPnl: number;
}

export default function Backtest() {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasResults, setHasResults] = useState(false);
  
  // Config
  const [config, setConfig] = useState({
    strategy: 'grid-trading',
    pair: 'ETH/USDT',
    exchange: 'binance',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    initialCapital: 10000,
    leverage: 1,
    fees: 0.1,
  });

  // Mock results
  const results: BacktestResult = {
    totalReturn: 47.3,
    winRate: 58.2,
    maxDrawdown: -12.4,
    sharpeRatio: 1.87,
    totalTrades: 342,
    profitableTrades: 199,
    avgWin: 2.3,
    avgLoss: -1.8,
    largestWin: 8.7,
    largestLoss: -5.2,
    profitFactor: 1.63,
  };

  const trades: Trade[] = [
    { id: '1', timestamp: '2024-01-05 14:32', type: 'buy', price: 2250.50, amount: 0.5, pnl: 0, cumPnl: 0 },
    { id: '2', timestamp: '2024-01-05 16:45', type: 'sell', price: 2285.00, amount: 0.5, pnl: 17.25, cumPnl: 17.25 },
    { id: '3', timestamp: '2024-01-06 09:12', type: 'buy', price: 2270.00, amount: 0.6, pnl: 0, cumPnl: 17.25 },
    { id: '4', timestamp: '2024-01-06 11:30', type: 'sell', price: 2240.00, amount: 0.6, pnl: -18.00, cumPnl: -0.75 },
    { id: '5', timestamp: '2024-01-07 08:00', type: 'buy', price: 2210.00, amount: 0.7, pnl: 0, cumPnl: -0.75 },
    { id: '6', timestamp: '2024-01-07 15:20', type: 'sell', price: 2280.00, amount: 0.7, pnl: 49.00, cumPnl: 48.25 },
  ];

  const runBacktest = () => {
    setIsRunning(true);
    setProgress(0);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsRunning(false);
          setHasResults(true);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Strategy Backtester</h1>
          <p className="text-[#848e9c]">Test your trading strategies with historical data</p>
        </div>
        <div className="flex items-center gap-3">
          {hasResults && (
            <button className="btn-secondary btn-md">
              <Download className="h-4 w-4" />
              Export Report
            </button>
          )}
          <button
            onClick={runBacktest}
            disabled={isRunning}
            className="btn-primary btn-md"
          >
            {isRunning ? (
              <>
                <div className="spinner !h-4 !w-4" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run Backtest
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="col-span-1">
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-5 w-5 text-[#f0b90b]" />
              <h2 className="font-semibold text-white">Configuration</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">Strategy</label>
                <select
                  value={config.strategy}
                  onChange={(e) => setConfig({ ...config, strategy: e.target.value })}
                  className="select"
                >
                  <option value="grid-trading">Grid Trading</option>
                  <option value="dca">DCA Accumulation</option>
                  <option value="mean-reversion">Mean Reversion</option>
                  <option value="momentum">Momentum Trading</option>
                  <option value="arbitrage">DEX Arbitrage</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">Trading Pair</label>
                <select
                  value={config.pair}
                  onChange={(e) => setConfig({ ...config, pair: e.target.value })}
                  className="select"
                >
                  <option value="ETH/USDT">ETH/USDT</option>
                  <option value="BTC/USDT">BTC/USDT</option>
                  <option value="SOL/USDT">SOL/USDT</option>
                  <option value="ARB/USDT">ARB/USDT</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">Start Date</label>
                  <input
                    type="date"
                    value={config.startDate}
                    onChange={(e) => setConfig({ ...config, startDate: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">End Date</label>
                  <input
                    type="date"
                    value={config.endDate}
                    onChange={(e) => setConfig({ ...config, endDate: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">Initial Capital ($)</label>
                <input
                  type="number"
                  value={config.initialCapital}
                  onChange={(e) => setConfig({ ...config, initialCapital: parseFloat(e.target.value) })}
                  className="input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">Leverage</label>
                  <select
                    value={config.leverage}
                    onChange={(e) => setConfig({ ...config, leverage: parseFloat(e.target.value) })}
                    className="select"
                  >
                    <option value="1">1x</option>
                    <option value="2">2x</option>
                    <option value="3">3x</option>
                    <option value="5">5x</option>
                    <option value="10">10x</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#848e9c] uppercase mb-2">Fees (%)</label>
                  <input
                    type="number"
                    value={config.fees}
                    onChange={(e) => setConfig({ ...config, fees: parseFloat(e.target.value) })}
                    className="input"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Progress */}
            {isRunning && (
              <div className="mt-6 pt-4 border-t border-white/[0.06]">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#848e9c]">Processing...</span>
                  <span className="text-white">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-[#181a20] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#f0b90b] transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="col-span-2 space-y-6">
          {!hasResults ? (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#f0b90b]/10 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-[#f0b90b]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No Backtest Results</h3>
              <p className="text-[#848e9c] mb-4">Configure your strategy and click "Run Backtest" to see results</p>
            </div>
          ) : (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-4">
                <div className="card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-[#0ecb81]" />
                    <span className="text-xs text-[#848e9c]">Total Return</span>
                  </div>
                  <div className="text-2xl font-bold text-[#0ecb81]">+{results.totalReturn}%</div>
                  <div className="text-xs text-[#5e6673] mt-1">
                    ${(config.initialCapital * (1 + results.totalReturn / 100)).toLocaleString()}
                  </div>
                </div>
                <div className="card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-[#1e90ff]" />
                    <span className="text-xs text-[#848e9c]">Win Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{results.winRate}%</div>
                  <div className="text-xs text-[#5e6673] mt-1">
                    {results.profitableTrades}/{results.totalTrades} trades
                  </div>
                </div>
                <div className="card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="h-4 w-4 text-[#f6465d]" />
                    <span className="text-xs text-[#848e9c]">Max Drawdown</span>
                  </div>
                  <div className="text-2xl font-bold text-[#f6465d]">{results.maxDrawdown}%</div>
                </div>
                <div className="card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-4 w-4 text-[#f0b90b]" />
                    <span className="text-xs text-[#848e9c]">Sharpe Ratio</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{results.sharpeRatio}</div>
                </div>
              </div>

              {/* Chart Placeholder */}
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Equity Curve</h3>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1 text-xs text-[#f0b90b] bg-[#f0b90b]/10 rounded-lg">Portfolio</button>
                    <button className="px-3 py-1 text-xs text-[#848e9c] hover:text-white hover:bg-white/[0.04] rounded-lg">Benchmark</button>
                  </div>
                </div>
                <div className="h-64 bg-[#12161c] rounded-xl flex items-center justify-center">
                  <div className="text-center text-[#5e6673]">
                    <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Chart visualization</p>
                  </div>
                </div>
              </div>

              {/* Detailed Stats */}
              <div className="grid grid-cols-2 gap-6">
                <div className="card p-5">
                  <h3 className="font-semibold text-white mb-4">Performance Metrics</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Total Trades', value: results.totalTrades },
                      { label: 'Profitable Trades', value: results.profitableTrades },
                      { label: 'Profit Factor', value: results.profitFactor },
                      { label: 'Avg Win', value: `+${results.avgWin}%`, color: '#0ecb81' },
                      { label: 'Avg Loss', value: `${results.avgLoss}%`, color: '#f6465d' },
                      { label: 'Largest Win', value: `+${results.largestWin}%`, color: '#0ecb81' },
                      { label: 'Largest Loss', value: `${results.largestLoss}%`, color: '#f6465d' },
                    ].map((stat) => (
                      <div key={stat.label} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
                        <span className="text-sm text-[#848e9c]">{stat.label}</span>
                        <span className="text-sm font-medium" style={{ color: stat.color || '#eaecef' }}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Trades */}
                <div className="card p-5">
                  <h3 className="font-semibold text-white mb-4">Recent Trades</h3>
                  <div className="space-y-2">
                    {trades.map((trade) => (
                      <div key={trade.id} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center",
                            trade.type === 'buy' ? "bg-[#0ecb81]/10" : "bg-[#f6465d]/10"
                          )}>
                            {trade.type === 'buy' ? (
                              <TrendingUp className="h-4 w-4 text-[#0ecb81]" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-[#f6465d]" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white capitalize">{trade.type}</div>
                            <div className="text-xs text-[#5e6673]">{trade.timestamp}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-white">${trade.price.toLocaleString()}</div>
                          {trade.pnl !== 0 && (
                            <div className={cn(
                              "text-xs",
                              trade.pnl > 0 ? "text-[#0ecb81]" : "text-[#f6465d]"
                            )}>
                              {trade.pnl > 0 ? '+' : ''}{trade.pnl.toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
